import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Menu, X, LogOut, Home, Receipt, DollarSign, Users, UserCircle, Building2, CreditCard, Package, KeyRound, ClipboardList, Download, Truck, Building, ShoppingCart, Landmark, Tags, Network } from 'lucide-react';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { NotificationBell } from './NotificationBell';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { scopeLabel } from '../utils/requestTheme';

const roleColors = {
  purchasing: 'text-blue-600',
  staff_gudang: 'text-orange-600',
  staff_clerk: 'text-amber-700',
  account_payable: 'text-emerald-600',
  internal: 'text-purple-600',
  operasional: 'text-[#800000]',
  approval: 'text-indigo-600',
  admin: 'text-slate-600'
};

const roleNames = {
  purchasing: 'Purchasing',
  staff_gudang: 'Staff Gudang',
  staff_clerk: 'Staff Clerk',
  account_payable: 'Account Payable',
  internal: 'Internal',
  operasional: 'Operasional',
  approval: 'Approval',
  admin: 'Admin'
};

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const [showChangePw, setShowChangePw] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { warning: sessionWarning, extend: extendSession } = useSessionTimeout();

  const menuItems = [
    { name: 'Dashboard', path: '/', icon: Home, roles: ['purchasing', 'staff_gudang', 'staff_clerk', 'account_payable', 'approval', 'admin'] },
    { name: 'Suppliers', path: '/suppliers', icon: Truck, roles: ['purchasing', 'staff_clerk', 'admin'] },
    { name: 'Companies', path: '/companies', icon: Building, roles: ['purchasing', 'staff_clerk', 'admin'] },
    { name: 'Purchase Orders', path: '/purchase-orders', icon: ShoppingCart, roles: ['purchasing', 'staff_gudang', 'staff_clerk', 'account_payable', 'admin'] },
    { name: 'Kode Bank', path: '/bank-codes', icon: Landmark, roles: ['purchasing', 'admin'] },
    { name: 'Data VA', path: '/virtual-accounts', icon: CreditCard, roles: ['purchasing', 'admin'] },
    { name: 'Daftar Barang', path: '/products', icon: Package, roles: ['purchasing', 'admin'] },
    { name: 'Invoices', path: '/invoices', icon: Receipt, roles: ['staff_clerk', 'account_payable', 'admin'] },
    { name: 'Payment Requests', path: '/payment-requests', icon: DollarSign, roles: ['account_payable', 'approval', 'admin'] },
    { name: 'Tipe Request', path: '/request-types', icon: Tags, roles: ['approval', 'admin'] },
    { name: 'Data Penerima', path: '/recipients', icon: UserCircle, roles: ['approval', 'admin'] },
    { name: 'Proses Bank', path: '/bank-process', icon: Building2, roles: ['approval', 'admin'] },
    { name: 'Upload Bukti', path: '/upload-bukti', icon: Download, roles: ['approval', 'admin'] },
    { name: 'Departemen', path: '/departments', icon: Network, roles: ['admin'] },
    { name: 'Users', path: '/users', icon: Users, roles: ['admin'] },
    { name: 'Audit Log', path: '/audit-log', icon: ClipboardList, roles: ['admin'] }
  ];

  const filteredMenuItems = menuItems.filter(item => item.roles.includes(user?.role));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        {sessionWarning && (
          <div className="bg-amber-100 text-amber-900 text-sm px-4 py-2 flex items-center justify-between" data-testid="session-warning">
            <span>Sesi akan berakhir dalam 1 menit karena tidak ada aktivitas.</span>
            <button className="underline font-semibold" onClick={extendSession} data-testid="session-extend-btn">Tetap masuk</button>
          </div>
        )}
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-md hover:bg-slate-100"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <h1 className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Manrope, sans-serif' }}>HISA</h1>
          </div>
          
          <div className="flex items-center gap-4">
            <NotificationBell />
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold text-slate-900">{user?.name}</p>
              <p className={`text-xs ${roleColors[user?.role]}`} data-testid="user-role-label">{scopeLabel(user) || roleNames[user?.role]}</p>
            </div>
            {!['internal', 'operasional'].includes(user?.role) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowChangePw(true)}
                title="Ganti Password"
                data-testid="change-password-button"
              >
                <KeyRound className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>
      <ChangePasswordDialog open={showChangePw} onOpenChange={setShowChangePw} />

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-30
          w-64 bg-white border-r border-slate-200
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          mt-[57px] lg:mt-0
        `}>
          <nav className="p-4 space-y-2">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 rounded-md text-left
                    transition-colors duration-150
                    ${isActive 
                      ? 'bg-hl text-slate-900 font-semibold' 
                      : 'text-slate-700 hover:bg-hl'
                    }
                  `}
                >
                  <Icon size={20} />
                  <span style={{ fontFamily: 'DM Sans, sans-serif' }}>{item.name}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8">
          {children}
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}

